/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { createEntityRoleStoreHouse } from 'src/luis/core/stores/entityRoleStoreHouse';
import { createUtteranceStoreHouse } from 'src/luis/projects/utterances/stores/utteranceStoreHouse';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';

/**
 * An entity store factory function.
 *
 * @param id The id of the entity to create.
 * @param entity The entity data.
 * @returns A new instance of an entity store.
 */
export const createEntityStore = (id: LuisId, entity: Partial<Omit<EntityStore, 'entityRoleStoreHouse'>>): EntityStore => {
    const entityStore: EntityStore = {
        id,
        typeId: entity.typeId !== undefined ? entity.typeId : ModelType.Simple,
        name: entity.name,
        entityRoleStoreHouse: createEntityRoleStoreHouse(),
        utteranceStoreHouse: createUtteranceStoreHouse({
            pageSize: LuisConstants.ResourceLimits.DefaultPageSize,
            totalSize: entity.labelsCount || 0
        }),

        /**
         * Computes labels count based on the utterance store house total size.
         */
        get labelsCount() {
            return this.utteranceStoreHouse.totalSize;
        },

        /**
         * Renames the entity with the given name.
         *
         * @param name The name to rename the entity with.
         */
        rename(name) {
            this.name = name;
        }
    };

    return observable(entityStore, { name: observable, rename: action }, { deep: false });
};
